import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  listAll 
} from 'firebase/storage';
import { firebaseConfig } from '../config/database.js';
import { initializeApp } from 'firebase/app';

// Firebase'i başlat
const app = initializeApp(firebaseConfig);
export const storage = getStorage(app);

// Storage klasör yapısı
export const STORAGE_PATHS = {
  PRODUCTS: 'images/products',
  SERVICES: 'images/services', 
  REFERENCES: 'images/references',
  NEWS: 'images/news',
  GALLERY: 'images/gallery',
  DOCUMENTS: 'images/documents'
};

/**
 * Dosya upload fonksiyonu
 * @param {File} file - Upload edilecek dosya
 * @param {string} folder - Klasör yolu (STORAGE_PATHS'den biri)
 * @param {string} fileName - Dosya adı (opsiyonel)
 * @returns {Promise<{url: string, path: string}>}
 */
export const uploadImage = async (file, folder, fileName = null) => {
  try {
    // Dosya adı oluştur
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const finalFileName = fileName || `${timestamp}.${fileExtension}`;
    
    // Storage referansı oluştur
    const storageRef = ref(storage, `${folder}/${finalFileName}`);
    
    // Dosyayı upload et
    console.log(`📤 Uploading image to: ${folder}/${finalFileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    
    // Download URL'ini al
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    console.log('✅ Image uploaded successfully:', downloadURL);
    
    return {
      url: downloadURL,
      path: `${folder}/${finalFileName}`,
      size: snapshot.metadata.size,
      contentType: snapshot.metadata.contentType
    };
    
  } catch (error) {
    console.error('❌ Image upload failed:', error);
    throw new Error(`Image upload failed: ${error.message}`);
  }
};

/**
 * Dosya silme fonksiyonu
 * @param {string} filePath - Silinecek dosyanın path'i
 * @returns {Promise<boolean>}
 */
export const deleteImage = async (filePath) => {
  try {
    const storageRef = ref(storage, filePath);
    await deleteObject(storageRef);
    console.log('✅ Image deleted successfully:', filePath);
    return true;
  } catch (error) {
    console.error('❌ Image deletion failed:', error);
    return false;
  }
};

/**
 * Klasördeki tüm dosyaları listele
 * @param {string} folder - Listelenmek istenen klasör
 * @returns {Promise<Array>}
 */
export const listImages = async (folder) => {
  try {
    const storageRef = ref(storage, folder);
    const result = await listAll(storageRef);
    
    const images = await Promise.all(
      result.items.map(async (itemRef) => {
        const url = await getDownloadURL(itemRef);
        return {
          name: itemRef.name,
          path: itemRef.fullPath,
          url: url
        };
      })
    );
    
    return images;
  } catch (error) {
    console.error('❌ Failed to list images:', error);
    return [];
  }
};

/**
 * Çoklu dosya upload fonksiyonu
 * @param {FileList} files - Upload edilecek dosyalar
 * @param {string} folder - Klasör yolu
 * @returns {Promise<Array>}
 */
export const uploadMultipleImages = async (files, folder) => {
  try {
    const uploadPromises = Array.from(files).map(file => 
      uploadImage(file, folder)
    );
    
    const results = await Promise.all(uploadPromises);
    console.log(`✅ ${results.length} images uploaded successfully`);
    
    return results;
  } catch (error) {
    console.error('❌ Multiple image upload failed:', error);
    throw error;
  }
};

/**
 * Dosya boyutu kontrolü
 * @param {File} file - Kontrol edilecek dosya
 * @param {number} maxSizeMB - Maksimum boyut (MB)
 * @returns {boolean}
 */
export const validateFileSize = (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Dosya tipi kontrolü
 * @param {File} file - Kontrol edilecek dosya
 * @param {Array} allowedTypes - İzin verilen dosya tipleri
 * @returns {boolean}
 */
export const validateFileType = (file, allowedTypes = ['image/jpeg', 'image/png', 'image/webp']) => {
  return allowedTypes.includes(file.type);
};

/**
 * Dosya validasyon fonksiyonu
 * @param {File} file - Validate edilecek dosya
 * @param {Object} options - Validasyon seçenekleri
 * @returns {Object}
 */
export const validateImage = (file, options = {}) => {
  const {
    maxSizeMB = 5,
    allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
  } = options;
  
  const errors = [];
  
  if (!validateFileType(file, allowedTypes)) {
    errors.push(`Desteklenmeyen dosya tipi: ${file.type}`);
  }
  
  if (!validateFileSize(file, maxSizeMB)) {
    errors.push(`Dosya çok büyük: ${(file.size / 1024 / 1024).toFixed(2)}MB (Max: ${maxSizeMB}MB)`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Utility fonksiyonları
export const getImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
    };
    img.src = URL.createObjectURL(file);
  });
};

export const compressImage = (file, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

export default {
  uploadImage,
  deleteImage,
  listImages,
  uploadMultipleImages,
  validateImage,
  getImageDimensions,
  compressImage,
  STORAGE_PATHS
}; 